import { Id } from '@/infra/id'
import type { Ordering } from '@/infra/paging'
import { type DocumentArchival, DocumentRetention } from './DocumentRetention'
import { DocumentStage, type ManualAttestationReason } from './DocumentStage'
import { DocumentLinks } from './DocumentLinks'
import type {
  AdminDocumentsQuery,
  DocumentsSortField,
  JobDocumentsQuery,
  UploadDocumentOptions,
  UserDocumentsQuery,
} from './DocumentStore'
import {
  type DocumentTypeFieldResource,
  type DocumentTypeResource,
  type UploaderUserType as UploaderUserTypeResource,
} from '@/features/documents/api/resources/document-type'
import {
  type DocumentTypeField,
  type DocumentTypeProps,
  type UploaderUserType,
} from './DocumentType'
import {
  type CreateDocumentRequest,
  type DocumentResource,
  type FileDescriptorResource,
  type ListDocumentsRequest,
  type ManualVerificationReason,
  type SearchDocumentsFields,
  type SearchDocumentsRequest,
} from '@/features/documents/api/resources/document'
import { type DocumentAccessResource } from '@/features/documents/api/resources/document-access'
import { match } from '@/utils/pattern-match-util'
import { maybeParseDate } from '@/utils/date-util'
import type { DocumentProps } from './Document'

/**
 * Maps a legacy document resource to document props, except the models it
 * refers to.
 */
export function mapDocumentResourceToProps(
  resource: DocumentResource,
): Partial<DocumentProps> {
  return {
    id: resource.long_id,
    title: resource.title,
    canEdit: resource.can_edit,
    description: resource.description,
    workspaceId: resource.workspace_id,
    archived: resource.archived,
    ephemeral: resource.ephemeral,
    stage: resource.latest_version
      ? mapFileDescriptorToDocumentStage(resource.latest_version.files[0])
      : undefined,
    retention:
      resource.archived !== undefined
        ? mapDocumentRetentionResourceToProps(resource)
        : undefined,
    links: resource.accesses
      ? mapAccessesToDocumentLinks(resource.accesses)
      : resource.access
        ? mapAccessesToDocumentLinks([resource.access])
        : undefined,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
  }
}

/**
 * Maps a document type resource to document type props.
 */
export function mapDocumentTypeResourceToProps(
  resource: DocumentTypeResource,
): DocumentTypeProps {
  return {
    id: resource.short_id,
    longId: resource.id,
    name: resource.name,
    heading: resource.heading,
    shortHelpText: resource.short_help_text,
    longHelpText: resource.long_help_text,
    imageUrl: resource.image_url || '',
    previewUrl: resource.preview_url || '',
    uploaderUserType: mapUploaderUserTypeResourceToUploaderUserType(
      resource.uploader_user_type,
    ),
    fields: (resource.fields ?? []).map(mapDocumentTypeFieldResourceToProps),
  }
}

function mapDocumentTypeFieldResourceToProps(
  resource: DocumentTypeFieldResource,
): DocumentTypeField {
  const base = {
    id: resource.id,
    label: resource.label,
    required: resource.required,
    description: resource.description,
    sortOrder: resource.sort_order,
  }
  switch (resource.type) {
    case 'BOOL':
      return { ...base, type: 'Bool' }
    case 'DATE':
      return { ...base, type: 'Date' }
    case 'TEXT':
      return { ...base, type: 'Text', isMulti: resource.is_multi }
    case 'NUMERIC':
      return { ...base, type: 'Numeric', isInteger: resource.is_integer }
    case 'OBJECT':
      return {
        ...base,
        type: 'Object',
        fields: resource.fields.map(mapDocumentTypeFieldResourceToProps),
      }
    case 'ARRAY':
      return {
        ...base,
        type: 'Array',
        fields: resource.fields.map(mapDocumentTypeFieldResourceToProps),
      }
  }
}

function mapUploaderUserTypeResourceToUploaderUserType(
  resource: UploaderUserTypeResource,
): UploaderUserType {
  return match<UploaderUserTypeResource, UploaderUserType>(resource, {
    CLIENT: 'Client',
    PRO: 'Pro',
  })
}

/**
 * Maps a search documents query to the legacy find documents query.
 */
export function mapSearchQueryToListDocumentsRequest(
  query: JobDocumentsQuery | UserDocumentsQuery,
): ListDocumentsRequest {
  const base = {
    limit: query.limit,
    after: query.after,
    archived: query.archived,
    workspace_id: query.workspaceId,
    order_by: mapOrderByToOrderings(query.orderBy),
  }

  return query.context === 'Job'
    ? { ...base, context: 'Job', job_id: Id.assert(query.jobId) }
    : { ...base, context: 'User' }
}

/**
 * Maps a search documents query to the legacy search documents query.
 */
export function mapSearchQueryToSearchDocumentsRequest(
  query: AdminDocumentsQuery,
): SearchDocumentsRequest {
  return {
    search_text: query.searchText || undefined,
    owner_public_ids: query.ownerPublicIds?.map(Id.assert),
    workspace_ids: query.workspaceIds?.map(Id.assert),
    job_ids: query.jobIds?.map(Id.assert),
    document_type_ids: query.documentTypeIds?.map(Id.assert),
    archived: query.archived,
    limit: query.limit,
    after: query.after,
    order_by: mapOrderByToOrderings(query.orderBy),
  }
}

/**
 * Maps the single ordering to the legacy ordering list.
 */
function mapOrderByToOrderings(
  orderBy: JobDocumentsQuery['orderBy'],
): Ordering<SearchDocumentsFields>[] | undefined {
  return orderBy
    ? [
        {
          field: match<DocumentsSortField, SearchDocumentsFields>(
            orderBy.field,
            {
              Title: 'title',
              DateCreated: 'date_created',
              DateModified: 'date_modified',
            },
          ),
          order: orderBy.direction,
        } satisfies Ordering<SearchDocumentsFields>,
      ]
    : undefined
}

/**
 * Maps a v3 document file state to the document stage.
 */
export function mapFileDescriptorToDocumentStage(
  file: FileDescriptorResource | undefined,
): DocumentStage {
  if (!file) {
    return DocumentStage.initial
  }

  return match<FileDescriptorResource['state'], DocumentStage>(file.state, {
    Pending: DocumentStage.initial,
    Scanning: DocumentStage.initial,
    VerifyingClientData: { type: 'AttestationScreening' },
    ManualVerificationRequired: () => ({
      type: 'ManualAttestationRequired',
      reason: mapManualVerificationReasonToManualAttestationReason(
        file.manual_verification_reason,
      ),
    }),
    Downloadable: { type: 'Released' },
    Purged: { type: 'Released' },
    Failed: { type: 'Rejected', rejection: 'ConversionFailed' },
  })
}

/**
 * Maps a v3 document manual verification reason to the document manual attestation reason.
 */
export function mapManualVerificationReasonToManualAttestationReason(
  reason?: ManualVerificationReason,
): ManualAttestationReason | undefined {
  if (!reason) {
    return undefined
  }

  return match(reason, {
    ClientNameMismatch: () => 'ClientNameMismatch',
    MissingProSignature: () => 'MissingProSignature',
  })
}

/**
 * Maps the accesses of a document resource to the document links.
 */
export function mapAccessesToDocumentLinks(
  accesses: DocumentAccessResource[] | undefined,
): DocumentLinks {
  if (!accesses) {
    return DocumentLinks.empty
  }

  return {
    job: {
      jobIds: accesses
        .filter((access) => access.type === 'Job')
        .map((access) => access.job_id),
    },
    workRequest: { workRequestId: null },
    strategy: { folderId: null },
  }
}

/**
 * Maps a document resource to the document retention.
 */
export function mapDocumentRetentionResourceToProps(
  resource: Partial<DocumentResource>,
): DocumentRetention {
  const archival = mapDocumentArchivalResourceToProps(resource)

  if (resource.latest_version?.purged) {
    // Purge details are not exposed by the API and probably we don't need it for the old API.
    return { type: 'Purged', archival, purge: null }
  }

  if (resource.archived) {
    return { type: 'Archived', archival }
  }

  return DocumentRetention.active
}

/**
 * Maps an upload document request to the legacy create document payload.
 * The title comes from the uploaded file's name.
 */
export function mapUploadRequestToCreateDocumentRequest(
  request: UploadDocumentOptions,
): CreateDocumentRequest {
  return {
    workspace_id: Id.assert(request.workspaceId),
    title: request.file.name,
    document_type_id: request.documentTypeId,
    access: request.jobId
      ? { type: 'Job', job_id: Id.assert(request.jobId) }
      : undefined,
  }
}

/**
 * Maps the archival fields of a document resource to the archival information.
 */
function mapDocumentArchivalResourceToProps(
  resource: Partial<DocumentResource>,
): DocumentArchival | null {
  if (!resource.archived_date || !resource.archived_by_user_public_id) {
    return null
  }

  return {
    archivedAt: new Date(resource.archived_date),
    archivedBy: Id(resource.archived_by_user_public_id),
  }
}
